import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import { JUNIOR_ALUMNI_MIN_YEAR, currentYear } from '../../shared/juniorAlumni'

import { PlainInput } from '../PlainField'
import StatesField from '../StatesField'

import styles from './style.module.scss'

/**
 * Junior Company Teilnahme (Anforderung #181): Hat die Unterstützer:in selbst
 * eines der Programme von Junior Österreich absolviert?
 *
 * Ein Schieber Ja/Nein, bei „Ja" darunter Schule, Jahr der Teilnahme und die
 * Bundesländer — dieselbe Auswahl wie bei den relevanten Bundesländern. Die
 * Überschrift setzt die einbindende Seite (Abschnitt 4 der Registrierung,
 * Karte im Fenster der Verwaltung …), hier steht nur die Frage.
 *
 * Kontrolliert: `value` trägt die vier Felder wie das Backend
 * (`juniorAlumni`, `juniorAlumniSchool`, `juniorAlumniYear`,
 * `juniorAlumniStates`), `onChange` bekommt nur die geänderten. `errors` sind
 * i18n-Schlüssel je Feld (`shared/juniorAlumni` → `juniorAlumniErrors`).
 *
 * Der Schieber ist ein `<button role="switch">` und kein Kontrollkästchen:
 * Auf der Registrierungsseite ist das erste Kontrollkästchen die Einwilligung.
 */
export default function JuniorAlumniField({ value, onChange, errors = {}, disabled, className }) {
	const { t } = useTranslation()
	const on = value?.juniorAlumni === true
	const unanswered = value?.juniorAlumni === null || value?.juniorAlumni === undefined
	const errorText = key => (errors[key] ? t(errors[key], { min: JUNIOR_ALUMNI_MIN_YEAR, max: currentYear() }) : null)

	return (
		<div className={clsx(styles.root, className)}>
			<div className={styles.questionRow}>
				<span className={styles.question} id="junior-alumni-question">
					{t('juniorAlumniQuestion')}
				</span>
				<span className={styles.switchRow}>
					{/* Die Wörter daneben setzen den Wert direkt — wer noch nichts
					    angegeben hat, kommt so mit einem Klick auf „Nein". */}
					<button
						type="button"
						tabIndex={-1}
						disabled={disabled}
						className={clsx(styles.switchText, !on && !unanswered && styles.switchTextOn)}
						onClick={() => onChange({ juniorAlumni: false })}
					>
						{t('No')}
					</button>
					<button
						type="button"
						role="switch"
						aria-checked={on}
						aria-labelledby="junior-alumni-question"
						name="juniorAlumni"
						disabled={disabled}
						className={clsx(styles.switch, on && styles.switchOn)}
						onClick={() => onChange({ juniorAlumni: !on })}
					>
						<span className={styles.knob} />
					</button>
					<button
						type="button"
						tabIndex={-1}
						disabled={disabled}
						className={clsx(styles.switchText, on && styles.switchTextOn)}
						onClick={() => onChange({ juniorAlumni: true })}
					>
						{t('Yes')}
					</button>
				</span>
			</div>

			{/* Bestand von vor der Frage: „nicht angegeben" ist kein Nein. */}
			{unanswered && <p className={styles.hint}>{t('juniorAlumniUnanswered')}</p>}

			{on && (
				<div className={styles.details}>
					<div className={styles.grid}>
						<PlainInput
							label={t('juniorAlumniSchool')}
							required
							name="juniorAlumniSchool"
							disabled={disabled}
							value={value.juniorAlumniSchool || ''}
							error={errorText('juniorAlumniSchool')}
							onChange={e => onChange({ juniorAlumniSchool: e.target.value })}
						/>
						{/* Ohne min/max am Feld: Der Browser hielte das Absenden sonst
						    mit eigener Blase an, und die Meldung am Feld bliebe veraltet. */}
						<PlainInput
							label={t('juniorAlumniYear')}
							required
							name="juniorAlumniYear"
							type="number"
							inputMode="numeric"
							step={1}
							disabled={disabled}
							value={value.juniorAlumniYear ?? ''}
							error={errorText('juniorAlumniYear')}
							onChange={e => onChange({ juniorAlumniYear: e.target.value })}
						/>
					</div>
					<StatesField
						label={t('juniorAlumniStates')}
						hint={t('juniorAlumniStatesHint')}
						value={value.juniorAlumniStates || []}
						error={errorText('juniorAlumniStates')}
						onChange={states => onChange({ juniorAlumniStates: states })}
					/>
				</div>
			)}
		</div>
	)
}

JuniorAlumniField.propTypes = {
	value: PropTypes.shape({
		juniorAlumni: PropTypes.bool,
		juniorAlumniSchool: PropTypes.string,
		juniorAlumniYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		juniorAlumniStates: PropTypes.arrayOf(PropTypes.string)
	}),
	onChange: PropTypes.func.isRequired,
	errors: PropTypes.object,
	disabled: PropTypes.bool,
	className: PropTypes.string
}
